import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { EDIT_WEBSITE_CONTENT_BASE_PATH } from '../routes/editWebsiteContentPaths';
import {
  buildCreatePostPath,
  CREATE_POST_BASE_PATH,
} from '../routes/createPostPaths';
import { buildReelsPath } from '../routes/reelPaths';
import { UPLOAD_VIDEOS_BASE_PATH } from '../routes/uploadVideoPaths';
import { SocialMediaManagementModal } from '../components/SocialMediaManagementModal';
import { dashboardApi, type DMDashboardData } from '../api/dashboardApi';
import { getApiErrorMessage } from '../utils/apiResponse';
import {
  ArrowRight,
  Check,
  Loader2,
  RefreshCw,
  Sparkles,
  WifiOff,
} from 'lucide-react';
import './marketing-command-dashboard.css';
import classNames from 'classnames';

const MaterialIcon = ({
  name,
  className = '',
  size,
}: {
  name: string;
  className?: string;
  size?: string;
}) => (
  <span
    aria-hidden="true"
    className={`mcd-material-icon ${className}`}
    style={size ? { fontSize: size } : undefined}
  >
    {name}
  </span>
);

const schoolImage =
  '/admissions-assured/uploads/Admission Counsellor Step By Step Guide.webp';

const actions: Array<[string, string, string, string?]> = [
  ['campaign', 'School Branding & Promotions', '', EDIT_WEBSITE_CONTENT_BASE_PATH],
  ['volume_up', 'Ad Campaigns', 'Ads Manager', '/digital-marketing/send-wishes'],
  ['edit_note', 'Create New Post', 'AI copy & visuals', buildCreatePostPath('create')],
  ['movie', 'Generate Reels', 'AI reel generator', buildReelsPath('create')],
  ['article', 'Generate Blogs', 'SEO-ready drafts', '/digital-marketing/blog'],
  ['upload', 'Upload Videos', 'Photos & videos', UPLOAD_VIDEOS_BASE_PATH],
];

const STATIC_CALENDAR_ITEMS = [
  { id: 1, time: '6:30 PM', title: 'Annual Day Highlights Reel', status: 'QUEUED' },
  { id: 2, time: '8:00 AM', title: 'Admissions Open 2026–27', status: 'LIVE' },
  { id: 3, time: '4:00 PM', title: 'Sports Day Highlights', status: 'QUEUED' },
];

const formatNumber = (value: number | null | undefined, compact = false) =>
  value == null
    ? '—'
    : new Intl.NumberFormat('en-IN', {
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: 1,
    }).format(value);

const Metric = ({
  label,
  value,
  trend,
  comingSoon = false,
}: {
  label: string;
  value: string;
  trend?: string;
  comingSoon?: boolean;
}) => (
  <div
    className="mcd-mini-metric"
    data-coming-soon={comingSoon ? '' : undefined}
  >
    <span>{label}</span>
    <strong>{value}</strong>
    {trend && <em>{trend}</em>}
  </div>
);

const WebsiteMetric = ({
  label,
  value,
  trend,
}: {
  label: string;
  value: string;
  trend: string;
}) => (
  <div className="flex h-[7.35vh] min-w-0 flex-col justify-center rounded-[0.843vh] border border-solid border-[#e5e8ef] bg-[#f8f9fb] px-[0.723vh] py-[0.602vh]">
    <span className="whitespace-normal text-[1.3vh] leading-[1.2] text-[#788297]">
      {label}
    </span>
    <strong
      className={`${label === 'Avg Session' ? 'text-[2vh]' : 'text-[2.5vh]'
        } font-bold leading-none text-[#172139]`}
    >
      {value}
    </strong>
    <em className="text-[1.2vh] font-semibold not-italic leading-[1.2] text-[#00a961]">
      {trend}
    </em>
  </div>
);

const PostMetric = ({
  label,
  value,
  comingSoon = false,
}: {
  label: string;
  value: string;
  comingSoon?: boolean;
}) => (
  <div
    data-coming-soon={comingSoon ? '' : undefined}
    className="flex min-w-0 flex-col justify-center rounded-[0.843vh] border border-solid border-[#e5e8f0] bg-[#f8f9fc] px-[0.843vh] py-[0.482vh]"
  >
    <span className="text-[1.3vh] leading-[1.2] text-[#6d7890]">
      {label}
    </span>
    <strong className="text-[2vh] font-bold leading-none text-[#182239]">
      {value}
    </strong>
  </div>
);

const seoIssueTones = {
  critical: 'bg-[#ffe9e9] text-[#e82f34]',
  high: 'bg-[#ffe9e9] text-[#e82f34]',
  medium: 'bg-[#fff3d9] text-[#e58c00]',
  low: 'bg-[#e4faef] text-[#00a866]',
} as const;

const SeoIssueChip = ({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: keyof typeof seoIssueTones;
}) => (
  <span
    data-tone={tone}
    className={`mcd-seo-issue-chip rounded-[0.602vh] px-[0.843vh] py-[0.482vh] text-[1.25vh] ${seoIssueTones[tone]}`}
  >
    {children}
  </span>
);

const birthdayMetricTones = {
  neutral: 'border-transparent bg-[#eef0fe] text-[#4f46e5]',
  success: 'border-transparent bg-[#e6f7ec] text-[#16a34a]',
  pink: 'border-transparent bg-[#fde8ef] text-[#ec4899]',
  violet: 'border-transparent bg-[#eef0fe] text-[#4f46e5]',
  orange: 'border-transparent bg-[#fff1e0] text-[#f59e0b]',
  processing: 'border-transparent bg-[#e6f7ec] text-[#4f46e5]',
} as const;

const BirthdayMetric = ({
  label,
  value,
  tone,
  processing = false,
  comingSoon = false,
}: {
  label: string;
  value: string;
  tone: keyof typeof birthdayMetricTones;
  processing?: boolean;
  comingSoon?: boolean;
}) => (
  <div
    data-coming-soon={comingSoon ? '' : undefined}
    data-tone={tone}
    className={classNames(`flex min-h-[7.1vh] flex-col items-center justify-center rounded-[0.964vh] border border-solid py-[0.602vh] text-center ${birthdayMetricTones[tone]}`)}
  >
    <strong
      className={classNames(`font-extrabold leading-none ${processing ? 'text-[1.8vh]' : 'text-[2.65vh]'
        }`)}
    >
      {value}
    </strong>
    <span
      className={classNames(`mt-[0.241vh] leading-[1.566vh] ${processing
        ? 'text-[1.8vh] font-bold text-[#00a25e]'
        : 'text-[1.55vh] text-[#7d8799]'
        }`)}
    >
      {label}
    </span>
  </div>
);

const SectionTitle = ({
  number,
  children,
  action,
}: {
  number: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className="mcd-section-title">
    <span className="mcd-number">{number}</span>
    <strong>{children}</strong>
    {action}
  </div>
);

export function MarketingCommandDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = useAuth();
  const [briefPlaying, setBriefPlaying] = React.useState(false);
  const [device, setDevice] = React.useState<'Desktop' | 'Tablet' | 'Mobile'>(
    'Tablet'
  );
  const [toast, setToast] = React.useState('');
  const [socialMediaModalOpen, setSocialMediaModalOpen] = React.useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = React.useState('');
  const [dashboard, setDashboard] = React.useState<DMDashboardData | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [websitePreviewLoading, setWebsitePreviewLoading] = React.useState(true);
  const [websitePreviewError, setWebsitePreviewError] = React.useState(false);
  const [websiteReachable, setWebsiteReachable] = React.useState(false);
  const [websiteRetryKey, setWebsiteRetryKey] = React.useState(0);
  const [comingSoonTooltip, setComingSoonTooltip] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const isDashboardRoute =
    location.pathname === '/digital-marketing/dashboard' ||
    location.pathname === '/digital-marketing/dashboard/';
  const act = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };
  const goTo = (path: string) => navigate(path);
  const loadDashboard = React.useCallback(async () => {
    if (!isDashboardRoute) {
      return;
    }
    setIsLoading(true);
    setLoadError('');
    try {
      setDashboard(await dashboardApi.view());
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Unable to load dashboard'));
    } finally {
      setIsLoading(false);
    }
  }, [isDashboardRoute]);

  React.useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const websiteUrl = React.useMemo(() => {
    const domain = authData?.BRANCH_DOMAIN?.trim();
    if (!domain) return '';
    return /^https?:\/\//i.test(domain) ? domain : `https://${domain}/`;
  }, [authData?.BRANCH_DOMAIN]);

  React.useEffect(() => {
    setWebsitePreviewLoading(true);
    setWebsitePreviewError(false);
    setWebsiteReachable(false);

    if (!websiteUrl) {
      setWebsitePreviewLoading(false);
      setWebsitePreviewError(true);
      return;
    }

    let active = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10000);

    fetch(websiteUrl, { mode: 'no-cors', signal: controller.signal })
      .then(() => {
        if (!active) return;
        window.clearTimeout(timer);
        setWebsiteReachable(true);
      })
      .catch(() => {
        if (!active) return;
        window.clearTimeout(timer);
        setWebsitePreviewLoading(false);
        setWebsitePreviewError(true);
      });

    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [websiteRetryKey, websiteUrl]);

  const retryWebsitePreview = () => {
    setDevice('Desktop');
    setWebsiteRetryKey(value => value + 1);
  };

  const openSchoolWebsite = () => {
    if (!websiteUrl) {
      act('School website is not configured');
      return;
    }
    window.open(websiteUrl, '_blank', 'noopener,noreferrer');
  };
  const campaigns = dashboard?.kpi_summary?.campaigns_live;
  const leads = dashboard?.kpi_summary?.qualified_leads;
  const socialReach = dashboard?.kpi_summary?.social_reach;
  const websiteVisitors = dashboard?.kpi_summary?.website_visitors;
  const hoursSaved = dashboard?.kpi_summary?.hours_saved;
  const socialPosts = dashboard?.content_centre?.social_posts;
  const parentPosts = dashboard?.content_centre?.parent_posts;
  const birthdays = dashboard?.content_centre?.birthdays;
  const calendar = dashboard?.content_calendar;
  const adoption = dashboard?.parent_app_adoption;
  const digitalAvatar = dashboard?.digital_avatar;
  const seoHealth = dashboard?.seo_health;
  // Contract-based: Campaigns and Leads are backed by complete API fields;
  // the remaining KPI responses are explicitly unavailable in NewDM.php.
  const kpiComingSoon = [false, false, true, true, true];
  const displayCalendarDays = React.useMemo(
    () =>
      dashboard
        ? calendar?.days || []
        : [
          '2026-06-25',
          '2026-06-26',
          '2026-06-27',
          '2026-06-28',
          '2026-06-29',
          '2026-06-30',
          '2026-07-01',
        ].map(date => ({ date, count: 2 })),
    [calendar?.days, dashboard]
  );
  React.useEffect(() => {
    if (!displayCalendarDays.length) {
      setSelectedCalendarDate('');
      return;
    }
    if (!displayCalendarDays.some(day => day.date === selectedCalendarDate)) {
      setSelectedCalendarDate(displayCalendarDays[0].date);
    }
  }, [displayCalendarDays, selectedCalendarDate]);

  const kpis: Array<[string, string, string, string, string, string?]> = [
    [
      'campaign',
      'Campaigns Live',
      campaigns?.available ? formatNumber(campaigns.count) : '12',
      campaigns?.available
        ? `${formatNumber(campaigns.launching_today)} Launching Today`
        : '3 Launching Today',
      'ai: 2 need budget review',
      undefined,
    ],
    [
      'group',
      'Qualified Leads',
      leads?.available ? formatNumber(leads.count) : '248',
      leads?.available ? `${formatNumber(leads.today)} Today` : '↑ 32 Today',
      'ai: 12 leads going cold',
      '/digital-marketing/leads',
    ],
    [
      'share',
      'Social Reach',
      socialReach?.available ? formatNumber(socialReach.count, true) : '842K',
      socialReach?.available && socialReach.change_percentage != null
        ? `${socialReach.change_percentage >= 0 ? '↑' : '↓'} ${Math.abs(
          socialReach.change_percentage
        )}%`
        : '↑ 18% vs last week',
      'ai: reels drive 62% of reach',
      '/digital-marketing/leads',
    ],
    [
      'language',
      'Website Visitors',
      websiteVisitors?.available
        ? formatNumber(websiteVisitors.count)
        : '12,486',
      websiteVisitors?.available && websiteVisitors.change_percentage != null
        ? `${websiteVisitors.change_percentage >= 0 ? '↑' : '↓'} ${Math.abs(
          websiteVisitors.change_percentage
        )}%`
        : '↑ 14% this week',
      'ai: mostly Instagram & Search',
      undefined,
    ],
    [
      'schedule',
      'Hours Saved',
      hoursSaved?.available ? `${formatNumber(hoursSaved.hours)}h` : '84h',
      hoursSaved?.available
        ? `₹${formatNumber(hoursSaved.amount_saved)} saved`
        : '₹42,500 saved',
      'ai: 84h across 5 channels',
      undefined,
    ],
  ];

  return (
    <div
      className="mcd-page"
      onPointerMove={event => {
        const target = (event.target as HTMLElement).closest<HTMLElement>(
          '[data-coming-soon]'
        );
        setComingSoonTooltip(
          target ? { x: event.clientX, y: event.clientY } : null
        );
      }}
      onPointerLeave={() => setComingSoonTooltip(null)}
      style={{
        width: '100%',
        height: '100%',
        maxWidth: 'none',
        padding: '0.723vh 0.964vh 2.41vh',
        overflowY: 'auto',
      }}
    >
      {isLoading && !dashboard && (
        <div className="mcd-toast" role="status">
          <Loader2 className="animate-spin" size="1.807vh" /> Loading dashboard…
        </div>
      )}
      {loadError && (
        <div className="mcd-toast" role="alert">
          {loadError}
          <button type="button" onClick={loadDashboard}>
            Retry
          </button>
        </div>
      )}
      {toast && (
        <div className="mcd-toast" role="status">
          <Check size="1.807vh" />
          {toast}
        </div>
      )}
      <section className="mcd-top-grid">
        <div className="mcd-intro">
          <div className="mcd-heading-row">
            <h1>Digital Marketing</h1>
            <span className="mcd-ai-agent-badge">
              <MaterialIcon name="auto_awesome" size="1.687vh" /> AI AGENT
            </span>
          </div>
          <div className="mcd-brief-row">
            <button
              className={briefPlaying ? 'mcd-voice active' : 'mcd-voice'}
              data-coming-soon
              onClick={() => setBriefPlaying(v => !v)}
              aria-label="Play today's voice brief"
            >
              <i className="mcd-voice-mic">
                <MaterialIcon name="mic" size="1.928vh" />
              </i>
              <MaterialIcon name="graphic_eq" size="2.892vh" />
              <span>
                <b>Voice Brief</b>
                <small>
                  {briefPlaying
                    ? 'Playing today’s brief…'
                    : 'Tap to hear today’s brief'}
                </small>
              </span>
            </button>
            <button
              className="mcd-link !text-[1.5vh]"
              data-coming-soon
              onClick={() => act('Opening all tasks')}
            >
              View All Tasks <ArrowRight size="1.687vh" />
            </button>
          </div>
        </div>
        {kpis.map(
          ([icon, label, value, delta, note, path], index) => (
            <article
              className="mcd-kpi"
              key={label}
              role={path ? 'button' : undefined}
              tabIndex={path ? 0 : undefined}
              aria-label={path ? `Open ${label}` : undefined}
              onClick={path ? () => goTo(path) : undefined}
              onKeyDown={
                path
                  ? event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      goTo(path);
                    }
                  }
                  : undefined
              }
              style={path ? { cursor: 'pointer' } : undefined}
            >
              <div className="mcd-kpi-label">
                <MaterialIcon name={icon} size="1.687vh" />
                <span>{label}</span>
                <svg className="mcd-kpi-ring" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="9.5" className="track" />
                  <circle cx="12" cy="12" r="9.5" className={`value tone-${index}`} />
                </svg>
              </div>
              <strong data-coming-soon={kpiComingSoon[index] ? '' : undefined}>
                {value}
              </strong>
              <b
                className={`tone-${index}`}
                data-coming-soon={kpiComingSoon[index] ? '' : undefined}
              >
                {delta}
              </b>
              <small data-coming-soon>{note}</small>
            </article>
          )
        )}
      </section>

      <section className="mcd-actions">
        {actions.map(([icon, label, sub, path], index) => (
          <button
            key={label}
            onClick={() =>
              path ? goTo(path) : act(`${label} is coming soon`)
            }
          >
            <i className={`action-${index}`}>
              <MaterialIcon name={icon} size="2.289vh" />
            </i>
            <span>
              <b>{label}</b>
              <small>{sub}</small>
            </span>
          </button>
        ))}
      </section>

      <section className="mcd-command-card">
        <header>
          <h2>
            AI Content Command Centre
          </h2>
          <button
            type="button"
            className="mcd-whatsapp-campaigns"
            data-coming-soon
            onClick={() => act('Feedback and Suggestions is coming soon')}
            aria-label="Feedback and Suggestions, coming soon"
          >
            <span className="mcd-feedback-dot" aria-hidden="true" />
            Feedback and Suggestions
          </button>
        </header>
        <div className="mcd-command-grid">
          <article className="mcd-content-panel">
            <SectionTitle
              number="01"
              action={
                <button
                  className="mcd-text-button"
                  onClick={() => goTo(CREATE_POST_BASE_PATH)}
                >
                  View All <ArrowRight size="1.566vh" />
                </button>
              }
            >
              Today&apos;s Posts on School&apos;s Social Media
            </SectionTitle>
            <div className="mcd-social-box">
              <div
                className="mcd-recommend"
                data-coming-soon={
                  socialPosts?.ai_recommendation?.available ? undefined : ''
                }
              >
                <span className="mcd-recommend-label">
                  <MaterialIcon name="auto_awesome" size="1.446vh" />
                  AI RECOMMENDED VIDEO
                </span>{' '}
                <b>
                  {socialPosts?.ai_recommendation?.available
                    ? `${socialPosts.ai_recommendation.confidence}% CONFIDENCE`
                    : '96% CONFIDENCE'}
                </b>
              </div>
              <div className="mcd-post-row">
                <div className="mcd-thumb">
                  <img
                    src={
                      socialPosts?.items?.[0]?.thumbnail ||
                      socialPosts?.items?.[0]?.thumbnail_url ||
                      schoolImage
                    }
                    alt={socialPosts?.items?.[0]?.title || 'School event preview'}
                  />
                  <MaterialIcon name="play_arrow" size="1.928vh" />
                </div>
                <div className="mcd-post-copy">
                  <h3>
                    {socialPosts?.items?.[0]?.title ||
                      'Annual Day Highlights Reel'}
                  </h3>
                  <p className="!text-[1.1vh]">
                    {socialPosts?.items?.[0]?.description ||
                      'A glimpse of our Annual Day celebration filled with joy.'}
                  </p>
                  <div className="mcd-post-stats">
                    <PostMetric
                      label="TIME"
                      value={
                        socialPosts?.ai_recommendation?.recommended_time ||
                        '6:30 PM'
                      }
                      comingSoon={!socialPosts?.ai_recommendation?.available}
                    />
                    <PostMetric
                      label="REACH"
                      value={
                        socialPosts?.ai_recommendation?.available
                          ? formatNumber(
                            socialPosts.ai_recommendation.predicted_reach,
                            true
                          )
                          : '14.8K'
                      }
                      comingSoon={!socialPosts?.ai_recommendation?.available}
                    />
                    <PostMetric
                      label="LEADS"
                      value={
                        socialPosts?.ai_recommendation?.available
                          ? formatNumber(
                            socialPosts.ai_recommendation.predicted_leads
                          )
                          : '+48'
                      }
                      comingSoon={!socialPosts?.ai_recommendation?.available}
                    />
                  </div>
                </div>
              </div>
              <div className="mcd-reasons-actions">
                <div
                  className="mcd-reasons"
                  data-coming-soon={
                    socialPosts?.ai_recommendation?.available ? undefined : ''
                  }
                >
                  <b>Why AI Recommends This</b>
                  <span>
                    <Check size="1.446vh" /> Parents most active between 6–8 PM
                  </span>
                  <span>
                    <Check size="1.446vh" /> Annual Day content is trending
                    locally
                  </span>
                  <span>
                    <Check size="1.446vh" />
                    {`Similar reels generated + 32% engagement`}
                  </span>
                </div>
                <button
                  className="mcd-create"
                  onClick={() => goTo(buildReelsPath('create'))}
                >
                  Create Now <MaterialIcon name="auto_awesome" size="1.566vh" />
                </button>
              </div>
            </div>
          </article>

          <article className="mcd-content-panel">
            <SectionTitle
              number="02"
              action={
                <>
                  <span className="mcd-today">
                    {String(parentPosts?.count ?? 1).padStart(2, '0')} TODAY
                  </span>
                  <button
                    className="mcd-text-button"
                    onClick={() => goTo('/digital-marketing/send-wishes')}
                  >
                    View All <ArrowRight size="1.566vh" />
                  </button>
                </>
              }
            >
              Today&apos;s Posts for Parents on Portal
            </SectionTitle>
            <div className="mcd-story" data-coming-soon>
              <span className="mcd-story-label">
                <Sparkles size="1.325vh" /> INSPIRE 365 · TODAY&apos;S STORY
              </span>
              <div className="mcd-story-main">
                <div className="mcd-avatar">
                  AK
                  <MaterialIcon name="play_arrow" size="1.205vh" />
                </div>
                <div>
                  <h3>Dr. A.P.J. Abdul Kalam</h3>
                  <p>
                    How curiosity became innovation — inspiring students to
                    dream big.
                  </p>
                </div>
                <b>3 min</b>
              </div>
              <button
                className="mcd-listen"
                onClick={() => act('Playing today’s story')}
              >
                <span className="mcd-listen-mic">
                  <MaterialIcon name="mic" size="1.566vh" />
                </span>
                <b>Listen Now</b>
                <span className="mcd-waveform" aria-hidden="true">
                  {[
                    8, 14, 10, 17, 12, 20, 15, 11, 16, 21, 13, 18, 10, 15, 8,
                  ].map((height, index) => (
                    <i
                      key={`${height}-${index}`}
                      style={{ height: `${height / 8.3}vh` }}
                    />
                  ))}
                </span>
                <i className="mcd-listen-play">
                  <MaterialIcon name="play_arrow" size="1.807vh" />
                </i>
              </button>
            </div>
          </article>

          <article className="mcd-content-panel birthdays" data-coming-soon>
            <SectionTitle
              number="03"
              action={
                <button type="button" className="mcd-text-button mcd-birthday-view-all" onClick={() => goTo('/digital-marketing/send-wishes')}>
                  View All <ArrowRight size="1.8vh" />
                </button>
              }
            >
              Today&apos;s Student Birthdays
            </SectionTitle>
            <div
              className="mcd-birthday-grid"
              data-coming-soon={
                !isLoading && !birthdays?.available ? '' : undefined
              }
            >
              <BirthdayMetric
                label="Students Birthdays Today"
                value={birthdays?.available ? formatNumber(birthdays.students_today) : '8'}
                tone="neutral"
              />
              <BirthdayMetric
                label="Selfies Received"
                value={
                  birthdays?.selfies?.available
                    ? `${formatNumber(birthdays.selfies.received)} / ${formatNumber(
                      birthdays.students_today
                    )}`
                    : '5 / 8'
                }
                tone="success"
              />
              <BirthdayMetric
                label="Selfies Pending"
                value={
                  birthdays?.selfies?.available
                    ? formatNumber(birthdays.selfies.pending)
                    : '3'
                }
                tone="pink"
              />
              <BirthdayMetric
                label="Birthdays This Month"
                value="40"
                tone="violet"
              />
              <BirthdayMetric
                label="Selfies Received"
                value={birthdays?.selfies?.available ? formatNumber(birthdays.selfies.received) : '28'}
                tone="success"
              />
              <BirthdayMetric
                label="Days To Go"
                value="5"
                tone="orange"
              />
            </div>
            <button
              className="mcd-capture"
              data-coming-soon
              onClick={() => act('Faculty reminder sent')}
            >
              <span className="mcd-capture-icon">
                <MaterialIcon name="photo_camera" />
              </span>
              Inform Faculty to Capture Pictures
            </button>
            <p className="!text-[1.1vh]" data-coming-soon>
              <b>AI Insight:</b> 3 students are still awaiting birthday selfies.
              Reminders sent to pending faculty help publish reels before
              day&apos;s end.
            </p>
          </article>
        </div>
      </section>

      <section className="mcd-lower-grid">
        <article className="mcd-card mcd-website">
          <header>
            <div>
              <h2>
                <MaterialIcon name="public" size="1.928vh" /> Website Performance Hub
              </h2>
              <p>
                AI-generated responsive website • live analytics •
                recommendations
              </p>
            </div>
            <b className={websitePreviewError ? 'offline' : ''}>
              {websitePreviewLoading
                ? '● CHECKING'
                : websitePreviewError
                  ? '○ OFFLINE'
                  : '● LIVE'}
            </b>
          </header>
          <div className="mcd-website-body">
            <div className="mcd-site-column">
              <div className="mcd-device-tabs">
                {(['Desktop', 'Tablet', 'Mobile'] as const).map(d => (
                  <button
                    className={device === d ? 'active' : ''}
                    onClick={() => setDevice(d)}
                    key={d}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <div className={`mcd-site-preview is-${device.toLowerCase()}`}>
                {websitePreviewLoading && !websitePreviewError && (
                  <div className="mcd-preview-loading" role="status">
                    <Loader2 aria-hidden="true" />
                    <b>Loading website preview</b>
                    <small>Checking your live school website</small>
                  </div>
                )}
                {websitePreviewError && (
                  <div className="mcd-preview-offline" role="status">
                    <WifiOff aria-hidden="true" />
                    <b>{websiteUrl ? 'Preview unavailable' : 'Website not configured'}</b>
                    <small>
                      {websiteUrl
                        ? "The school website couldn't be reached right now"
                        : 'Add a branch domain to enable the live preview'}
                    </small>
                    <button type="button" onClick={retryWebsitePreview}>
                      <RefreshCw aria-hidden="true" /> Retry
                    </button>
                  </div>
                )}
                {websiteReachable && !websitePreviewError && (
                  <iframe
                    key={`${websiteUrl}-${websiteRetryKey}`}
                    src={websiteUrl}
                    title={`School website ${device.toLowerCase()} preview`}
                    sandbox="allow-scripts allow-same-origin"
                    scrolling="no"
                    onLoad={() => setWebsitePreviewLoading(false)}
                    onError={() => {
                      setWebsitePreviewLoading(false);
                      setWebsitePreviewError(true);
                    }}
                  />
                )}
              </div>
              <button
                className="mcd-edit-website"
                onClick={() => goTo(EDIT_WEBSITE_CONTENT_BASE_PATH)}
              >
                <MaterialIcon name="edit" size="1.566vh" /> Edit Website
              </button>
            </div>
            <div className="mcd-performance">
              <b data-coming-soon>
                PERFORMANCE SUMMARY <span>LIVE 14</span>
              </b>
              <div data-coming-soon>
                {[
                  ['Total Visitors', '12.4K', '↑ 14%'],
                  ['Total Users', '9.8K', '↑ 9%'],
                  ['Page Views', '38.2K', '↑ 11%'],
                  ['Avg Session', '3m 42s', '↑ 8%'],
                  ['Bounce Rate', '41.2%', '↑ 3.4%'],
                  ['Returning', '34%', '↑ 5%'],
                ].map(x => (
                  <WebsiteMetric
                    key={x[0]}
                    label={x[0]}
                    value={x[1]}
                    trend={x[2]}
                  />
                ))}
              </div>
              <button
                className="mcd-outline"
                onClick={openSchoolWebsite}
              >
                Open Live Website <ArrowRight size="1.566vh" />
              </button>
            </div>
          </div>
        </article>

        <article className="mcd-card mcd-avatar-card">
          <header>
            <h2>Digital Avatar</h2>
            <b>
              {!dashboard || digitalAvatar?.is_online
                ? '● ONLINE'
                : '○ OFFLINE'}
            </b>
          </header>
          <div
            className="mcd-avatar-photo"
            style={
              digitalAvatar?.thumbnail
                ? {
                  backgroundImage: `url(${digitalAvatar.thumbnail})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                }
                : undefined
            }
          >
            {!digitalAvatar?.thumbnail && 'AI'}
            <span className="mcd-avatar-status">
              <MaterialIcon name="person" size="1.566vh" /> AI RESPONDING
            </span>
          </div>
          <h3>24×7 Virtual Admissions Counsellor</h3>
          <p className="!text-[1.1vh]">
            Your face & voice • 9 Indian languages • Bhashini
          </p>
          <footer>
            <button onClick={() => goTo('/digital-marketing/avatar-selection')}>
              Change
            </button>
            <button
              className="primary"
              onClick={() => goTo('/digital-marketing/avatar-selection/create')}
            >
              Create
            </button>
          </footer>
        </article>

        <article className="mcd-card mcd-seo">
          <header>
            <div>
              <h2>SEO Health</h2>
              <p>
                {dashboard ? formatNumber(seoHealth?.total_issues) : '28'} issues
                found
              </p>
            </div>
            <button
              onClick={() => goTo('/digital-marketing/seo')}
              aria-label="Open SEO Health"
            >
              <ArrowRight size="1.566vh" />
            </button>
          </header>
          <div className="mcd-seo-body">
            <div className="mcd-score">
              <strong>
                {seoHealth?.overall_seo_percentage == null
                  ? '21.7%'
                  : `${seoHealth.overall_seo_percentage}%`}
              </strong>
              <small>SCORE</small>
            </div>
            <div className="flex flex-col gap-[0.723vh]">
              <SeoIssueChip tone="critical">
                • {seoHealth?.available ? formatNumber(seoHealth.CRITICAL) : '01'}{' '}
                Critical
              </SeoIssueChip>
              <SeoIssueChip tone="high">
                • {seoHealth?.available ? formatNumber(seoHealth.HIGH) : '05'} High
              </SeoIssueChip>
              <SeoIssueChip tone="medium">
                • {seoHealth?.available ? formatNumber(seoHealth.MEDIUM) : '12'}{' '}
                Medium
              </SeoIssueChip>
              <SeoIssueChip tone="low">
                • {seoHealth?.available ? formatNumber(seoHealth.LOW) : '03'} Low
              </SeoIssueChip>
            </div>
          </div>
          <button
            className="mcd-fix"
            onClick={() => goTo('/digital-marketing/seo')}
          >
            <MaterialIcon name="edit" size="1.687vh" /> Fix Issues Detected
          </button>
        </article>

        <article className="mcd-card mcd-calendar">
          <header data-coming-soon>
            <div>
              <h2>Content Calendar</h2>
              <p>
                AI Planner •{' '}
                {dashboard ? formatNumber(calendar?.scheduled_count) : '8'} scheduled
              </p>
            </div>
            <div className="mcd-calendar-nav">
              <button
                type="button"
                disabled
                aria-label="Previous week"
                data-coming-soon
              >
                <MaterialIcon name="chevron_left" size="1.446vh" />
              </button>
              <b>
                {calendar?.range?.start
                  ? new Date(`${calendar.range.start}T00:00:00`).toLocaleDateString(
                    'en-IN',
                    { month: 'short' }
                  )
                  : 'Jun'}
              </b>
              <button
                type="button"
                disabled
                aria-label="Next week"
                data-coming-soon
              >
                <MaterialIcon name="chevron_right" size="1.446vh" />
              </button>
            </div>
          </header>
          <div className="mcd-days" data-coming-soon>
            {displayCalendarDays.map(day => (
              <button
                type="button"
                className={selectedCalendarDate === day.date ? 'active' : ''}
                key={day.date}
                onClick={() => setSelectedCalendarDate(day.date)}
                aria-pressed={selectedCalendarDate === day.date}
                aria-label={`${new Date(`${day.date}T00:00:00`).toLocaleDateString(
                  'en-IN',
                  { weekday: 'long', day: 'numeric', month: 'long' }
                )}, ${day.count} scheduled items`}
              >
                <small>
                  {new Date(`${day.date}T00:00:00`)
                    .toLocaleDateString('en-IN', { weekday: 'short' })
                    .toUpperCase()}
                </small>
                {String(new Date(`${day.date}T00:00:00`).getDate()).padStart(
                  2,
                  '0'
                )}
                <i>{day.count ? '••' : ''}</i>
              </button>
            ))}
          </div>
          <div className="mcd-events" data-coming-soon>
            {STATIC_CALENDAR_ITEMS.map(item => (
              <p key={item.id}>
                <b>{item.time}</b>
                <span>{item?.title || 'Scheduled content'}</span>
                <em
                  className={
                    String(item?.status).toUpperCase() === 'LIVE' ? 'live' : ''
                  }
                >
                  {item?.status || 'QUEUED'}
                </em>
              </p>
            ))}
          </div>
          <button
            className="mcd-social-setup"
            onClick={() => setSocialMediaModalOpen(true)}
          >
            <MaterialIcon name="hub" size="1.928vh" />
            <span className="mcd-social-setup-copy">
              <b>School Branding &amp; Promotions</b>
              <small>Open Agent.AI Social Hub</small>
            </span>
            <MaterialIcon name="chevron_right" size="1.807vh" />
          </button>
        </article>
      </section>

      <div className="mcd-bottom-bar-row">
        <section className="mcd-adoption">
          <div className="mcd-download">
            <MaterialIcon name="download" size="2.289vh" />
          </div>
          <div className="mcd-adoption-title">
            <h3>Unregistered Parent App Downloads</h3>
            <p>Track & increase parent app adoption</p>
          </div>
          <div
            className="mcd-ring"
            data-coming-soon={
              !isLoading && !adoption?.available ? '' : undefined
            }
          >
            <b>
              {adoption?.available && adoption.conversion_percentage != null
                ? `${adoption.conversion_percentage}%`
                : '76%'}
            </b>
            <small>INSTALLED</small>
          </div>
          {[
            [
              'Installed',
              adoption?.available ? formatNumber(adoption.installed) : '521',
              adoption?.available && adoption.installed_change_percentage != null
                ? `${adoption.installed_change_percentage >= 0 ? '↑' : '↓'} ${Math.abs(
                  adoption.installed_change_percentage
                )}%`
                : '↑ 12%',
            ],
            [
              'Pending',
              adoption?.available ? formatNumber(adoption.pending) : '163',
              adoption?.available && adoption.pending_change_percentage != null
                ? `${adoption.pending_change_percentage >= 0 ? '↑' : '↓'} ${Math.abs(
                  adoption.pending_change_percentage
                )}%`
                : '↓ 8%',
            ],
            ['Invited', adoption?.available ? formatNumber(adoption.invited) : '684', ''],
            [
              'Conversion Rate',
              adoption?.available && adoption.conversion_percentage != null
                ? `${adoption.conversion_percentage}%`
                : '76%',
              adoption?.available && adoption.conversion_benchmark != null
                ? `Benchmark ${adoption.conversion_benchmark}%`
                : 'Below avg.',
            ],
          ].map(x => (
            <Metric
              key={x[0]}
              label={x[0]}
              value={x[1]}
              trend={x[2]}
              comingSoon={!isLoading && !adoption?.available}
            />
          ))}
          <div className="mcd-insight" data-coming-soon>
            <b>AI Insight</b>
            <p>
              163 parents have not installed the app. Classes 1–3 are below
              average — send reminders to improve engagement.
            </p>
          </div>
          <button
            className="mcd-reach-parents"
            data-coming-soon
            onClick={() => act('Reminders sent to remaining parents')}
          >
            <MaterialIcon name="chat" size="1.807vh" /> Reach Remaining Parents
          </button>
        </section>
        <div className="mcd-soon-tile mcd-soon-conversational" data-coming-soon>
          <span className="mcd-soon-icon">
            <MaterialIcon name="forum" />
          </span>
          <span className="mcd-soon-copy">
            <b>Conversational AI</b>
            <small><i />COMING SOON</small>
          </span>
        </div>
        <div className="mcd-soon-tile mcd-soon-branding" data-coming-soon>
          <span className="mcd-soon-icon">
            <MaterialIcon name="palette" />
          </span>
          <span className="mcd-soon-copy">
            <b>School Branding Materials</b>
            <small><i />COMING SOON</small>
          </span>
        </div>
      </div>
      <SocialMediaManagementModal
        isOpen={socialMediaModalOpen}
        onClose={() => setSocialMediaModalOpen(false)}
      />
      {comingSoonTooltip && (
        <div
          className="mcd-coming-soon-tooltip"
          role="tooltip"
          style={{
            left: Math.min(comingSoonTooltip.x + 14, window.innerWidth - 110),
            top: Math.min(comingSoonTooltip.y + 16, window.innerHeight - 42),
          }}
        >
          Coming Soon
        </div>
      )}
    </div>
  );
}
